import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { HelpCircle, X, LifeBuoy, BookOpen } from "lucide-react";
import { useLang } from "@/lib/LanguageContext";

export default function HelpButton({ bottomOffset = "calc(1rem + env(safe-area-inset-bottom, 0px))" }) {
  const [open, setOpen] = useState(false);
  const { lang } = useLang();

  const texts = {
    fr: { title: "Besoin d'aide ?", ticket: "Ticket support", faq: "FAQ" },
    en: { title: "Need help?", ticket: "Support ticket", faq: "FAQ" },
    es: { title: "Necesitas ayuda?", ticket: "Ticket soporte", faq: "FAQ" },
    ar: { title: "تحتاج مساعدة؟", ticket: "تذكرة دعم", faq: "الاسئلة" },
  };
  const t = texts[lang] || texts.fr;

  return (
    <div
      className="fixed right-4 z-30 flex flex-col items-end gap-2"
      style={{ bottom: bottomOffset }}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.85, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 10 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
            className="w-52 rounded-2xl border border-border bg-card p-4 shadow-elevated"
          >
            <p className="mb-3 text-sm font-semibold">{t.title}</p>
            <div className="space-y-2">
              <Link
                to="/support"
                className="flex items-center gap-2.5 rounded-xl bg-secondary px-3 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-primary/5"
              >
                <LifeBuoy className="h-4 w-4 shrink-0 text-primary" />
                {t.ticket}
              </Link>
              <Link
                to="/faq"
                className="flex items-center gap-2.5 rounded-xl bg-secondary px-3 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-primary/5"
              >
                <BookOpen className="h-4 w-4 shrink-0 text-primary" />
                {t.faq}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        onClick={() => setOpen((value) => !value)}
        whileTap={{ scale: 0.92 }}
        className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-brand transition-all hover:brightness-110"
        aria-label={t.title}
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}>
              <X className="h-5 w-5" />
            </motion.span>
          ) : (
            <motion.span key="help" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}>
              <HelpCircle className="h-5 w-5" />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    </div>
  );
}
